/**
 * Driver o'ng tugma menyusi: holat va rolga qarab (sof funksiya, test: menu.test.ts).
 * Ruxsat backend'da ham tekshiriladi; bu yerda foydalanuvchi bajara olmaydigan amal umuman ko'rsatilmaydi.
 */
import {
  Archive,
  ArrowRightLeft,
  BellPlus,
  Briefcase,
  CircleX,
  Copy,
  ExternalLink,
  Eye,
  FileText,
  Link2,
  MessageSquarePlus,
  Phone,
  PhoneCall,
  Truck,
  UserRoundCog,
} from "lucide-react";
import { STATUS_BG } from "@/components/ui/badge";
import { compactMenu, SEPARATOR, type MenuEntry } from "@/components/ui/context-menu";
import { can, isManager, type Me } from "@/features/auth/types";
import { profileItem, type ProfileAction } from "@/features/people/profileAction";
import { allowedTargets } from "./transitions";
import { LOST_STATUSES, type ApplicationBrief, type DriverListItem, type DriverStatus } from "./types";

export type Translate = (key: string, options?: Record<string, unknown>) => string;

export type DriverMenuTarget = Pick<DriverListItem, "id" | "full_name" | "status" | "phone" | "phone_display" | "current_job"> & { owner?: DriverListItem["owner"] };

export interface DriverMenuActions {
  open: () => void;
  openNewTab: () => void;
  copyLink: () => void;
  copy: (text: string) => void;
  call: (phone: string) => void;
  navigate: (path: string) => void;
  changeStatus: (to: DriverStatus) => void;
  /** Pipeline: "yo'qotilgan" oynasi (sabab bilan); bo'lsa yo'qotilgan statuslar ro'yxatdan chiqariladi */
  markLost?: () => void;
  logActivity: (type: "call" | "note") => void;
  addReminder: () => void;
  transfer: () => void;
  archive: () => void;
  /** Driver egasining profili (ruxsat bo'lsa) */
  profile?: ProfileAction;
}

/**
 * @param me  rol (hired -> orientation qaytarish -- menejer) va ruxsatlar (o'tkazish, arxivlash)
 * @param t   "menu" namespace tarjimoni
 * @param tc  "common" namespace (status nomlari)
 * @param mode "pipeline" -- "Change status" o'rniga "Move to", yo'qotilganlar alohida (sabab oynasi);
 *             "profile" -- profil sahifasining o'zida: "Open" yo'q (shu sahifa)
 */
export function driverMenu(d: DriverMenuTarget, me: Pick<Me, "role" | "permissions">, a: DriverMenuActions, t: Translate, tc: Translate, mode: "list" | "pipeline" | "profile" = "list"): MenuEntry[] {
  const manager = isManager(me.role);
  const pipeline = mode === "pipeline";
  const targets = allowedTargets(d.status, manager);
  const statusTargets = pipeline ? targets.filter((s) => !LOST_STATUSES.includes(s)) : targets;
  const job = d.current_job;
  return compactMenu([
    mode !== "profile" && { key: "open", label: t("open"), icon: Eye, onSelect: a.open },
    { key: "open-tab", label: t("openNewTab"), icon: ExternalLink, onSelect: a.openNewTab },
    SEPARATOR,
    !!d.phone && { key: "call", label: t("call"), icon: Phone, hint: d.phone_display || d.phone, onSelect: () => a.call(d.phone) },
    !!d.phone && { key: "copy-phone", label: t("copyPhone"), icon: Copy, onSelect: () => a.copy(d.phone_display || d.phone) },
    { key: "copy-link", label: t("copyLink"), icon: Link2, onSelect: a.copyLink },
    profileItem(a.profile, d.owner?.id, t, { name: d.owner?.display_name, key: "owner-profile" }),
    SEPARATOR,
    { key: "log-call", label: t("driver.logCall"), icon: PhoneCall, onSelect: () => a.logActivity("call") },
    { key: "add-note", label: t("driver.addNote"), icon: MessageSquarePlus, onSelect: () => a.logActivity("note") },
    { key: "add-reminder", label: t("driver.addReminder"), icon: BellPlus, onSelect: a.addReminder },
    mode !== "profile" && { key: "documents", label: t("driver.documents"), icon: FileText, onSelect: () => a.navigate(`/drivers/${d.id}?tab=documents`) },
    SEPARATOR,
    {
      key: "status",
      label: pipeline ? t("driver.moveTo") : t("driver.changeStatus"),
      icon: ArrowRightLeft,
      hint: tc(`status.${d.status}`),
      items: statusTargets.map((s) => ({ key: `status-${s}`, label: tc(`status.${s}`), dot: STATUS_BG[s], onSelect: () => a.changeStatus(s) })),
    },
    pipeline && !!a.markLost && targets.some((s) => LOST_STATUSES.includes(s)) && { key: "lost", label: t("driver.markLost"), icon: CircleX, onSelect: a.markLost },
    can(me, "drivers_transfer") && { key: "transfer", label: t("driver.transfer"), icon: UserRoundCog, onSelect: a.transfer },
    SEPARATOR,
    !!job && { key: "job", label: t("driver.openJob", { title: job.title }), icon: Briefcase, onSelect: () => a.navigate(`/jobs/${job.id}`) },
    !!job && { key: "carrier", label: t("driver.openCarrier", { name: job.carrier_name }), icon: Truck, onSelect: () => a.navigate(`/carriers/${job.carrier_id}`) },
    SEPARATOR,
    can(me, "drivers_archive") && { key: "archive", label: t("archive"), icon: Archive, danger: true, onSelect: a.archive },
  ]);
}

export interface ApplicationMenuActions {
  navigate: (path: string) => void;
  openNewTab: (path: string) => void;
  copy: (text: string) => void;
}

/** Driver profilidagi ariza (vakansiyaga yuborilgan): vakansiya, carrier, carrier javobi */
export function driverApplicationMenu(app: Pick<ApplicationBrief, "job" | "carrier_reply" | "note">, a: ApplicationMenuActions, t: Translate): MenuEntry[] {
  const jobPath = `/jobs/${app.job.id}`;
  return compactMenu([
    { key: "job", label: t("driver.openJob", { title: app.job.title }), icon: Briefcase, onSelect: () => a.navigate(jobPath) },
    { key: "job-tab", label: t("openNewTab"), icon: ExternalLink, onSelect: () => a.openNewTab(jobPath) },
    { key: "carrier", label: t("driver.openCarrier", { name: app.job.carrier_name }), icon: Truck, onSelect: () => a.navigate(`/carriers/${app.job.carrier_id}`) },
    SEPARATOR,
    !!app.carrier_reply && { key: "copy-reply", label: t("application.copyReply"), icon: Copy, onSelect: () => a.copy(app.carrier_reply) },
    !!app.note && { key: "copy-note", label: t("task.copyNote"), icon: Copy, onSelect: () => a.copy(app.note) },
  ]);
}
